const FitModes = {
    CONTAIN: 'contain',
    COVER: 'cover',
};

class ImageBackground {
    /**
     * 
     * @param {HTMLCanvasElement} canvas 
     */
    constructor(canvas) {
        let ctx = canvas.getContext('2d');
        this._canvas = canvas;
        this._ctx = ctx;
        this._pattern = null;

        this._img = null;
        this._offset = [0, 0];
        this._scale = [1, 1];
        this._fitMode = FitModes.COVER;
        this._tiling = true;
    }

    setImage(img) {
        if (img === this._img) return;

        this._img = img;
        this._updatePattern();
        this._update();
    }

    setOffset(x, y) {
        let offset = this._offset;
        if (x === offset[0] && y === offset[1]) return;

        offset[0] = x;
        offset[1] = y;
        this._update();
    }

    setScale(x, y) {
        let scale = this._scale;
        if (x === scale[0] && y === scale[1]) return;

        scale[0] = x;
        scale[1] = y;
        this._update();
    }

    setSize(w, h) {
        this._canvas.width = w;
        this._canvas.height = h;
        this._update();
    }

    setFitMode(fitMode) {
        this._fitMode = fitMode;
        this._update();
    }

    setTiling(tiling) {
        this._tiling = tiling;
        this._updatePattern();
        this._update();
    }

    _updatePattern() {
        this._pattern = this._tiling && this._img ? this._ctx.createPattern(this._img, 'repeat') : null;
    }

    _update() {
        saveProperties(this._ctx, ['fillStyle', 'fillColor'], () => {
            saveTransform(this._ctx, () => {
                const { width, height } = this._canvas;

                try { this._ctx.resetTransform(); }
                catch (err) { }

                if (this._img !== null) {
                    let imgWidth, imgHeight;

                    if (this._img.tagName === 'VIDEO') {
                        ({ videoWidth: imgWidth, videoHeight: imgHeight } = this._img);
                    } else {
                        ({ naturalWidth: imgWidth, naturalHeight: imgHeight } = this._img);
                    }

                    const [offset, scale] = [this._offset, this._scale];

                    let scaleToFit;

                    switch (this._fitMode) {
                        case FitModes.CONTAIN:
                            scaleToFit = Math.min(width / imgWidth, height / imgHeight);
                            break;

                        case FitModes.COVER:
                            scaleToFit = Math.max(width / imgWidth, height / imgHeight);
                            break;

                        default:
                            throw new Error('invalid fit mode: '+this._fitMode);
                    }

                    const centerPosPx = [(0.5 + offset[0]) * width, (0.5 + offset[1]) * height];

                    const pieceSizePx = [scale[0] * imgWidth * scaleToFit, scale[1] * imgHeight * scaleToFit];
                    const piecePosPx = [centerPosPx[0] - pieceSizePx[0] * 0.5, centerPosPx[1] - pieceSizePx[1] * 0.5];

                    if (this._pattern) {
                        this._ctx.clearRect(0, 0, width, height);

                        const drawOffset = piecePosPx;
                        const drawScale = [pieceSizePx[0] / imgWidth, pieceSizePx[1] / imgHeight];

                        const fillWidth = width / drawScale[0];
                        const fillHeight = height / drawScale[1];
                        const fillX = -drawOffset[0] / drawScale[0];
                        const fillY = -drawOffset[1] / drawScale[1];

                        this._ctx.translate(drawOffset[0], drawOffset[1]);
                        this._ctx.scale(drawScale[0], drawScale[1]);

                        this._ctx.fillStyle = this._pattern;
                        this._ctx.fillRect(fillX, fillY, fillWidth, fillHeight);
                    } else {
                        this._ctx.fillColor = '#000000';
                        this._ctx.fillRect(0, 0, width, height);
                        this._ctx.clearRect(piecePosPx[0], piecePosPx[1], pieceSizePx[0], pieceSizePx[1]);

                        this._ctx.drawImage(this._img, piecePosPx[0], piecePosPx[1], pieceSizePx[0], pieceSizePx[1]);
                    }
                }
            });
        });
    }
}

function saveProperties(ctx, props, callback) {
    const prevValues = props.map(prop => ctx[prop]);

    try {
        callback();
    } finally {
        for (let [i, prop] of props.entries()) {
            ctx[prop] = prevValues[i];
        }
    }
}

function saveTransform(ctx, callback) {
    try { prevTransform = this._ctx.getTransform(); }
    catch (err) { }

    try {
        callback();
    } finally {
        try { this._ctx.setTransform(prevTransform); }
        catch (err) { }
    }
}

ImageBackground.FitModes = FitModes;

export default ImageBackground;
